// PDF "Full dossier" export — the archival reference for a case.
//
// Structure (redesigned):
//   1. Cover page
//   2. Table of contents (with clickable entries to every section +
//      every record in Appendix A + every figure in Appendix B)
//   3. Case summary (cached or regenerated upstream)
//   4. Current status of the case
//   5. Key facts (with "Record #N" references styled + linked)
//   6. Parties involved
//   7. Chronological narrative (prose; story API output)
//   8. AI disclaimer
//   9. Appendix A — Full records (each on its own page, bookmarked,
//      with clickable "See Figure N" links to Appendix B when the
//      record has an attached image)
//   10. Appendix B — Figures (embedded images, bookmarked, each with
//       a "From Record #N" back-link to Appendix A)
//
// Rendered directly with jsPDF. Cross-links are resolved in two
// passes: a `destinations` map tracks the page number of every
// named anchor as it renders, and a `pendingLinks` queue records
// rectangles that should become clickable. After all content is
// laid out, the links pass writes jsPDF `link()` annotations with
// the resolved page numbers.

import { jsPDF } from "jspdf";
import type { CaseRecord, RecordAttachment } from "../types";
import { CASE_STATUS_LABELS } from "../types";
import {
  AI_DISCLAIMER,
  RECORD_REF_RE,
  buildStatusParagraph,
  copyrightLine,
  dateRangeLine,
  directionLabel,
  extractParties,
  fetchBinary,
  fetchImageAsDataUrl,
  formatDateTime,
  formatLong,
  formatShort,
  KEY_FACT_TYPE_LABEL,
  parseKeyFacts,
  recordBookmarkLabel,
  significanceLabel,
  slugify,
  stripProseTimestamps,
  triggerDownload,
  typeLabelFor,
  volumeDateRange,
  type ExportBundle,
  type VolumeBriefEntry,
} from "./exportShared";
import { renderPdfPages } from "./pdfThumbnail";
import { fileUrl } from "../api";
import { buildCostSummaryLines } from "./costsMath";

// Progress callback so the caller's modal can show "section N of M"
// (and per-record progress during the full-content appendix).
export type DossierProgress = (
  label: string,
  current: number,
  total: number,
) => void;

// Summary returned to the caller for the final "Dossier generated:
// N pages, M attachments included, K failed" report.
export interface DossierStats {
  pages: number;
  attachmentsIncluded: number;
  attachmentsFailed: number;
}

const MARGIN = 54;
const LINE = 14;
const COLOR_TEXT: [number, number, number] = [25, 25, 25];
const COLOR_MUTED: [number, number, number] = [110, 95, 70];
const COLOR_ACCENT: [number, number, number] = [150, 110, 60];
const COLOR_LINK: [number, number, number] = [40, 80, 160];
const COLOR_HEADING: [number, number, number] = [20, 20, 20];
const COLOR_BORDER: [number, number, number] = [200, 180, 140];
const COLOR_HEADER_FILL: [number, number, number] = [245, 242, 236];
const COLOR_RECORD_HEADER: [number, number, number] = [236, 236, 236];

interface SectionMark {
  title: string;
  page: number;
  // Depth 0 = top-level section, 1 = sub-item (records, figures).
  // Only depth 0 appears in the TOC's main list; sub-items render
  // in-place alongside their parent section's TOC group.
  depth: 0 | 1;
  // Named anchor this entry resolves to in the destinations map —
  // used both to register the destination and to render a TOC link
  // pointing at it. null = section not targeted by cross-links
  // (e.g. TOC itself).
  anchor: string | null;
}

interface PendingLink {
  // Page where the clickable rectangle lives.
  sourcePage: number;
  x: number;
  y: number;
  w: number;
  h: number;
  // Resolved against `destinations` at the end of render.
  targetAnchor: string;
}

export async function exportCaseDossierPdf(
  bundle: ExportBundle,
  authHeaders: Record<string, string> = {},
  onProgress?: DossierProgress,
): Promise<DossierStats> {
  let attachmentsIncluded = 0;
  let attachmentsFailed = 0;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const maxW = w - MARGIN * 2;
  let y = MARGIN;

  const sections: SectionMark[] = [];
  const recordMarks: Array<{ label: string; page: number; anchor: string }> =
    [];
  const destinations = new Map<string, number>();
  const pendingLinks: PendingLink[] = [];

  function currentPage(): number {
    return doc.getCurrentPageInfo().pageNumber;
  }

  function registerAnchor(name: string): void {
    if (!destinations.has(name)) destinations.set(name, currentPage());
  }

  function ensureRoom(lines = 1): void {
    if (y + LINE * lines > h - MARGIN - 24) {
      doc.addPage();
      y = MARGIN;
    }
  }

  function text(
    s: string,
    opts: {
      size?: number;
      bold?: boolean;
      italic?: boolean;
      color?: [number, number, number];
      indent?: number;
      lineHeight?: number;
      serif?: boolean;
    } = {},
  ): void {
    doc.setFontSize(opts.size ?? 10.5);
    const style = opts.bold
      ? opts.italic
        ? "bolditalic"
        : "bold"
      : opts.italic
        ? "italic"
        : "normal";
    // Serif (Times) for readable record/body prose; Helvetica elsewhere.
    doc.setFont(opts.serif ? "times" : "helvetica", style);
    const [r, g, b] = opts.color ?? COLOR_TEXT;
    doc.setTextColor(r, g, b);
    const lines = doc.splitTextToSize(s, maxW - (opts.indent ?? 0));
    const lh = opts.lineHeight ?? LINE;
    for (const line of lines) {
      ensureRoom();
      doc.text(line, MARGIN + (opts.indent ?? 0), y);
      y += lh;
    }
  }

  // Render prose while detecting "Record #N" references on each
  // wrapped line and queuing a clickable link rectangle for each
  // match. The link resolves to the record's anchor in Appendix A.
  // Uses monospace-style width measurement via getTextWidth() for
  // each pre-ref slice — adequate for Helvetica at body sizes.
  function textWithRecordRefs(
    s: string,
    opts: { size?: number; color?: [number, number, number] } = {},
  ): void {
    const size = opts.size ?? 10.5;
    doc.setFontSize(size);
    doc.setFont("helvetica", "normal");
    const [cr, cg, cb] = opts.color ?? COLOR_TEXT;
    doc.setTextColor(cr, cg, cb);
    const lines = doc.splitTextToSize(s, maxW);
    for (const line of lines) {
      ensureRoom();
      doc.text(line, MARGIN, y);
      // Scan this rendered line for Record #N references and queue
      // a link rectangle for each. The rectangle spans the matched
      // text's pixel width at the current font size.
      RECORD_REF_RE.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = RECORD_REF_RE.exec(line)) !== null) {
        const before = line.slice(0, m.index);
        const matchText = m[0];
        const xOffset = doc.getTextWidth(before);
        const width = doc.getTextWidth(matchText);
        // Draw an accent-coloured overlay on the reference so it
        // reads as a link. We overwrite the same text with the link
        // colour — cheaper than tracking tokens.
        const [ar, ag, ab] = COLOR_LINK;
        doc.setTextColor(ar, ag, ab);
        doc.text(matchText, MARGIN + xOffset, y);
        doc.setTextColor(cr, cg, cb); // restore for next iteration
        pendingLinks.push({
          sourcePage: currentPage(),
          x: MARGIN + xOffset,
          y: y - size * 0.8, // rough ascender offset so the click area lines up
          w: width,
          h: size + 2,
          targetAnchor: `record:${m[1]!}`,
        });
      }
      y += LINE;
    }
  }

  // Render a single clickable line ("See Figure N" / "From Record
  // #N" / TOC entries). Wraps the line in link colour and queues a
  // pendingLinks entry covering the drawn text.
  function linkLine(
    label: string,
    targetAnchor: string,
    opts: { size?: number; bold?: boolean; indent?: number } = {},
  ): void {
    const size = opts.size ?? 10;
    doc.setFontSize(size);
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setTextColor(...COLOR_LINK);
    ensureRoom();
    const indent = opts.indent ?? 0;
    doc.text(label, MARGIN + indent, y);
    const width = doc.getTextWidth(label);
    pendingLinks.push({
      sourcePage: currentPage(),
      x: MARGIN + indent,
      y: y - size * 0.8,
      w: width,
      h: size + 2,
      targetAnchor,
    });
    y += LINE;
  }

  function h1(
    title: string,
    anchor: string | null = null,
    registerInToc = true,
  ): void {
    if (currentPage() > 1 || y > MARGIN + 2) {
      doc.addPage();
      y = MARGIN;
    }
    if (registerInToc) {
      sections.push({ title, page: currentPage(), depth: 0, anchor });
    }
    if (anchor) registerAnchor(anchor);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLOR_HEADING);
    doc.text(title, MARGIN, y);
    y += 28;
    doc.setDrawColor(...COLOR_BORDER);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, y, w - MARGIN, y);
    y += 16;
  }

  function h2(title: string): void {
    ensureRoom(3);
    y += 4;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40, 40, 40);
    doc.text(title, MARGIN, y);
    y += 18;
  }

  function footer(pageNumber: number, total: number): void {
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(130, 120, 100);
    doc.text("Generated by CaseThread", MARGIN, h - 20);
    doc.text(`Page ${pageNumber} of ${total}`, w - MARGIN, h - 20, {
      align: "right",
    });
  }

  interface TableOpts {
    headers: string[];
    rows: string[][];
    widths: number[];
    headerFontSize?: number;
    bodyFontSize?: number;
  }

  function drawTable(opts: TableOpts): void {
    const headerSize = opts.headerFontSize ?? 9;
    const bodySize = opts.bodyFontSize ?? 9;
    const cellPadX = 4;
    const cellPadY = 4;
    const rowLineGap = 11;
    const colWidths = opts.widths.map((pct) => (pct / 100) * maxW);

    const drawHeader = () => {
      doc.setFillColor(...COLOR_HEADER_FILL);
      doc.setDrawColor(...COLOR_BORDER);
      doc.setLineWidth(0.4);
      const headerHeight = rowLineGap + cellPadY * 2;
      doc.rect(MARGIN, y, maxW, headerHeight, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(headerSize);
      doc.setTextColor(...COLOR_HEADING);
      let x = MARGIN;
      for (let i = 0; i < opts.headers.length; i++) {
        const header = opts.headers[i] ?? "";
        const width = colWidths[i] ?? 0;
        doc.text(header, x + cellPadX, y + cellPadY + rowLineGap - 3, {
          maxWidth: width - cellPadX * 2,
        });
        x += width;
      }
      y += headerHeight;
    };

    drawHeader();

    doc.setFont("helvetica", "normal");
    doc.setFontSize(bodySize);
    doc.setTextColor(...COLOR_TEXT);

    for (const row of opts.rows) {
      const wrapped: string[][] = row.map((val, i) => {
        const width = colWidths[i] ?? maxW;
        return doc.splitTextToSize(val || "", width - cellPadX * 2);
      });
      const maxLines = Math.max(1, ...wrapped.map((l) => l.length));
      const rowHeight = maxLines * rowLineGap + cellPadY * 2;

      if (y + rowHeight > h - MARGIN - 24) {
        doc.addPage();
        y = MARGIN;
        drawHeader();
        doc.setFont("helvetica", "normal");
        doc.setFontSize(bodySize);
        doc.setTextColor(...COLOR_TEXT);
      }

      doc.setDrawColor(...COLOR_BORDER);
      doc.setLineWidth(0.3);
      doc.rect(MARGIN, y, maxW, rowHeight);

      let x = MARGIN;
      for (let i = 0; i < row.length; i++) {
        const lines = wrapped[i] ?? [""];
        const width = colWidths[i] ?? 0;
        let cellY = y + cellPadY + rowLineGap - 3;
        for (const line of lines) {
          doc.text(line, x + cellPadX, cellY, {
            maxWidth: width - cellPadX * 2,
          });
          cellY += rowLineGap;
        }
        x += width;
      }
      y += rowHeight;
    }
    y += 8;
  }

  // -- Source data unpacking ------------------------------------------------

  const caseData = bundle.case;
  const records = bundle.records;
  const contacts = bundle.contacts;
  const volumes = bundle.volumes;
  const volumeBriefs = bundle.volumeBriefs;
  const keyFacts = parseKeyFacts(caseData);
  const parties = extractParties(records, contacts);
  const sortedRecords = [...records].sort((a, b) =>
    a.record_date.localeCompare(b.record_date),
  );

  // Build a date-anchored lookup so we can turn the AI's
  // [Record YYYY-MM-DD] citation format (per GROUNDING_RULES) into
  // the dossier's navigable form: "Record #N (Short date)". The
  // index is 1-based and matches Appendix A's numbering.
  // Provenance carries [rec:RECORD_ID] citations from the AI generation.
  // Map each record id to its 1-based Appendix A index so every cited
  // claim in the narrative becomes a "Record #N" cross-link.
  const recordIndexById = new Map<string, number>();
  for (let i = 0; i < sortedRecords.length; i++) {
    recordIndexById.set(sortedRecords[i]!.id, i + 1);
  }
  const transformRecordCitations = (raw: string): string => {
    return raw.replace(/\[rec:([^\]\s]+)\]/g, (match, id: string) => {
      const idx = recordIndexById.get(id);
      if (!idx) return ""; // unresolved id — drop the marker rather than leak it
      const r = sortedRecords[idx - 1]!;
      const shortDate = formatShort(r.record_date);
      return `Record #${idx} (${shortDate})`;
    });
  };

  // -- Cover ----------------------------------------------------------------

  doc.setFontSize(26);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLOR_HEADING);
  y = MARGIN + 110;
  {
    const titleLines = doc.splitTextToSize(caseData.name, maxW);
    for (const line of titleLines) {
      doc.text(line, w / 2, y, { align: "center" });
      y += 30;
    }
  }
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...COLOR_MUTED);
  doc.text("Full dossier", w / 2, y + 10, { align: "center" });
  y += 36;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLOR_ACCENT);
  doc.text(
    CASE_STATUS_LABELS[caseData.status] ?? caseData.status,
    w / 2,
    y,
    { align: "center" },
  );
  y += 30;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...COLOR_MUTED);
  {
    const attachmentCount = records.reduce(
      (n, r) =>
        n + (r.file_r2_key ? 1 : 0) + (r.attachments?.length ?? 0),
      0,
    );
    const coverMeta = [
      dateRangeLine(records),
      `${records.length} record${records.length === 1 ? "" : "s"} · ${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"}`,
      `Prepared ${formatDateTime(new Date().toISOString())} by CaseThread`,
    ];
    for (const line of coverMeta) {
      doc.text(line, w / 2, y, { align: "center" });
      y += 18;
    }
    // Deterministic fact count (computed by the caller).
    if (bundle.coverFactLine) {
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...COLOR_HEADING);
      doc.text(bundle.coverFactLine, w / 2, y, { align: "center" });
      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10.5);
      doc.setTextColor(...COLOR_MUTED);
    }
  }
  // Brief parties roll-up on the cover — up to 5 names so the cover
  // doesn't sprawl. Full list lives in the Parties Involved section.
  if (parties.length > 0) {
    y += 12;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...COLOR_HEADING);
    doc.text("Parties involved", w / 2, y, { align: "center" });
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLOR_MUTED);
    const topFive = parties.slice(0, 5);
    for (const p of topFive) {
      const line = p.role ? `${p.name} — ${p.role}` : p.name;
      doc.text(line, w / 2, y, { align: "center" });
      y += 14;
    }
    if (parties.length > topFive.length) {
      doc.setFont("helvetica", "italic");
      doc.text(
        `…and ${parties.length - topFive.length} more`,
        w / 2,
        y,
        { align: "center" },
      );
      y += 14;
    }
  }

  // Cover disclaimer strip.
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_MUTED);
  doc.text(
    "Generated by CaseThread · AI-assisted · verify all facts",
    w / 2,
    h - MARGIN,
    { align: "center" },
  );

  // -- Reserve the TOC pages (drawn at the end once we know layout) -------
  // TOC may span multiple pages for large cases. Reserve up to 3 pages
  // now; if the final TOC is shorter we leave them blank (minor cost
  // vs re-flowing the whole document).
  doc.addPage();
  const tocFirstPage = currentPage();
  doc.addPage();
  doc.addPage();
  y = MARGIN;

  // -- Case overview --------------------------------------------------------

  h1("Case overview", "section:summary");
  const overview = bundle.overview;
  if (!overview || overview.isEmpty) {
    text(
      "No facts have been extracted for this case yet. Run “Extract all facts” on the Overview tab to populate this section.",
      { size: 11, italic: true, color: COLOR_MUTED },
    );
  } else {
    if (overview.headerLine) {
      text(overview.headerLine, { size: 10.5, color: COLOR_MUTED });
    }
    text(overview.provenanceLegend, { size: 9.5, color: COLOR_MUTED });
    if (overview.parties.length) {
      h2("Parties involved");
      for (const p of overview.parties) text(`• ${p}`, { size: 10.5 });
    }
    if (overview.keyEvents.length) {
      h2("Key case events");
      for (const e of overview.keyEvents) text(e, { size: 10.5 });
    }
    if (overview.coverageTerms.length) {
      h2("Coverage terms");
      for (const t of overview.coverageTerms) text(`• ${t}`, { size: 10.5 });
    }
    if (overview.financialGroups.length) {
      h2("Financial summary");
      for (const g of overview.financialGroups) {
        text(g.header, { size: 11, bold: true });
        for (const l of g.lines) text(l, { size: 10.5 });
      }
    }
    if (overview.commitments.length) {
      h2("Commitments & deadlines");
      for (const cm of overview.commitments) text(cm, { size: 10.5 });
    }
  }

  // -- Current status -------------------------------------------------------

  h1("Current status", "section:status");
  text(buildStatusParagraph(caseData, records, volumeBriefs), {
    size: 11,
  });

  // -- Key facts ------------------------------------------------------------

  h1("Key facts", "section:key-facts");
  if (keyFacts.length === 0) {
    text("No key facts extracted yet.", {
      size: 11,
      italic: true,
      color: COLOR_MUTED,
    });
  } else {
    // KeyFact rows carry an optional `source` (YYYY-MM-DD of the
    // source record) but no significance score, so we sort by
    // source date for chronological flow. Facts without a source
    // date fall to the end in their server-returned order.
    const sorted = [...keyFacts].sort((a, b) => {
      const as = a.source ?? "\uFFFF";
      const bs = b.source ?? "\uFFFF";
      return as.localeCompare(bs);
    });
    for (const f of sorted) {
      ensureRoom(3);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...COLOR_ACCENT);
      doc.text(KEY_FACT_TYPE_LABEL[f.type] ?? f.type, MARGIN, y);
      y += LINE;
      textWithRecordRefs(transformRecordCitations(f.fact), { size: 10.5 });
      y += 4;
    }
  }

  // -- Cost summary ---------------------------------------------------------

  const costs = bundle.costs ?? null;
  if (costs && costs.items.length > 0) {
    h1("Cost summary", "section:costs");
    const sym = costs.currency_symbol || "$";
    // House math (lib/costsMath): integer cents, never the AI-claimed
    // total_usd; no total line when the set's arithmetic doesn't verify.
    // The strings come from buildCostSummaryLines verbatim, which is what
    // the export regression test covers.
    const lines = buildCostSummaryLines(costs.items, sym, costs.currency || "");
    if (lines.totalLine) {
      text(lines.totalLine, { size: 12, bold: true });
    } else if (lines.notice) {
      text(lines.notice, { size: 10.5, color: COLOR_MUTED });
    }
    y += 4;
    drawTable({
      headers: ["Date", "Description", "Category", "Amount"],
      widths: [16, 48, 18, 18],
      rows: costs.items.map((it, i) => [
        it.date ? formatShort(it.date) : "—",
        (it.description || "") + lines.descriptionSuffix(i),
        it.category,
        lines.amountStrings[i]!,
      ]),
    });
    if (costs.summary?.trim()) {
      text(stripProseTimestamps(costs.summary), { size: 10.5, serif: true });
    }
  }

  // -- Parties involved -----------------------------------------------------

  h1("Parties involved", "section:parties");
  if (parties.length === 0) {
    text("No parties identified yet.", {
      size: 11,
      italic: true,
      color: COLOR_MUTED,
    });
  } else {
    drawTable({
      headers: ["Name", "Role", "Email", "Mentions"],
      widths: [35, 25, 30, 10],
      rows: parties.map((p) => [
        p.aliases.length > 0
          ? `${p.name} (also: ${p.aliases.join(", ")})`
          : p.name,
        p.role ?? "",
        p.email ?? "",
        String(p.mentions),
      ]),
    });
  }

  // -- Chronological narrative ---------------------------------------------

  h1("Chronological narrative", "section:narrative");
  // Prefer the bundle's pre-rendered narrative (built by
  // renderProseFromEvents against the cached timeline) over
  // `case.story`, which is deprecated and no longer refreshed by the
  // export flow. Fall back to `case.story` only for the transition
  // window where a caller hasn't been updated to pass `narrative`.
  const narrative = (bundle.narrative ?? caseData.story ?? "").trim();
  if (narrative) {
    // Split on blank lines so paragraphs render with spacing. Record
    // references inside each paragraph become clickable via
    // textWithRecordRefs.
    const paragraphs = narrative.split(/\n\s*\n/);
    for (const p of paragraphs) {
      const trimmed = p.trim();
      if (!trimmed) continue;
      textWithRecordRefs(transformRecordCitations(trimmed), { size: 11 });
      y += 8;
    }
  } else {
    text(
      "Timeline not yet generated — open the Timeline tab and click Generate to produce a chronological narrative.",
      { size: 11, italic: true, color: COLOR_MUTED },
    );
  }

  // -- Volume summaries (multi-volume only) --------------------------------

  if (volumes.length > 1) {
    h1("Volume summaries", "section:volumes");
    const closed = volumeBriefs
      .filter((v) => v.volume.closed_at !== null)
      .sort((a, b) => a.volume.volume_number - b.volume.volume_number);
    if (closed.length === 0) {
      text("No closed volumes yet.", {
        size: 11,
        italic: true,
        color: COLOR_MUTED,
      });
    } else {
      for (const entry of closed) renderVolumeBrief(entry);
    }
  }

  // -- AI disclaimer --------------------------------------------------------

  h1("About this document", "section:disclaimer");
  text(AI_DISCLAIMER, { size: 10, italic: true, color: COLOR_MUTED });
  y += 6;
  text(copyrightLine(), { size: 9, color: COLOR_MUTED });

  // -- Appendix A — Record index -------------------------------------------

  h1("Appendix A — Record index", "section:appendix-a");
  if (sortedRecords.length === 0) {
    text("No records on file.", {
      size: 11,
      italic: true,
      color: COLOR_MUTED,
    });
  } else {
    renderRecordIndex(sortedRecords);
  }

  // -- Appendix B — Full record content ------------------------------------
  // Each record on its own page, with its full body and every
  // attachment embedded inline (images directly, PDF pages rendered
  // via pdfjs, audio noted with transcript, other files noted). Fetched
  // and processed one record at a time so a 100-record / 50-attachment
  // case doesn't balloon memory.

  h1("Appendix B — Full records", "section:appendix-b");
  if (sortedRecords.length === 0) {
    text("No records on file.", {
      size: 11,
      italic: true,
      color: COLOR_MUTED,
    });
  } else {
    for (let i = 0; i < sortedRecords.length; i++) {
      onProgress?.(
        `Embedding record ${i + 1} of ${sortedRecords.length}`,
        i + 1,
        sortedRecords.length,
      );
      await renderFullRecord(sortedRecords[i]!, i + 1);
    }
  }

  // -- TOC (drawn on the reserved pages) -----------------------------------

  doc.setPage(tocFirstPage);
  y = MARGIN;
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLOR_HEADING);
  doc.text("Contents", MARGIN, y);
  y += 28;
  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, w - MARGIN, y);
  y += 18;

  // Helper for a single TOC line. Page number is right-aligned; the
  // text portion is clickable when the anchor resolves.
  function tocLine(
    label: string,
    page: number,
    anchor: string | null,
    indent = 0,
    opts: { bold?: boolean } = {},
  ): void {
    ensureTocRoom();
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(11);
    const [lr, lg, lb] = anchor ? COLOR_LINK : COLOR_TEXT;
    doc.setTextColor(lr, lg, lb);
    const titleX = MARGIN + indent;
    const pageX = w - MARGIN;
    doc.text(label, titleX, y);
    doc.setTextColor(...COLOR_TEXT);
    doc.text(String(page), pageX, y, { align: "right" });
    if (anchor) {
      const width = doc.getTextWidth(label);
      pendingLinks.push({
        sourcePage: currentPage(),
        x: titleX,
        y: y - 9,
        w: width,
        h: 13,
        targetAnchor: anchor,
      });
    }
    y += 16;
  }

  function ensureTocRoom(): void {
    // Only advance within the reserved TOC range. If we overflow past
    // tocFirstPage+2 (three reserved pages), keep appending lines on
    // the third page's tail — not ideal but better than rewriting the
    // document.
    if (y > h - MARGIN - 24) {
      const next = currentPage() + 1;
      doc.setPage(next);
      y = MARGIN;
    }
  }

  for (const entry of sections) {
    tocLine(entry.title, entry.page, entry.anchor, 0, { bold: true });
    // Nest the per-record entries under Appendix B (full records).
    if (entry.anchor === "section:appendix-b") {
      for (const r of recordMarks) {
        tocLine(r.label, r.page, r.anchor, 16);
      }
    }
  }

  // -- PDF outline / nav-panel bookmarks -----------------------------------
  // Mirror the TOC structure: top-level section bookmark for each
  // section, nested bookmarks for every record and every figure.
  const outline = doc.outline;
  let appendixBNode: unknown = null;
  for (const entry of sections) {
    const node = outline.add(null, entry.title, { pageNumber: entry.page });
    if (entry.anchor === "section:appendix-b") appendixBNode = node;
  }
  // One child bookmark per record under Appendix B: "YYYY-MM-DD: Subject".
  if (appendixBNode) {
    for (const m of recordMarks) {
      outline.add(appendixBNode, m.label, { pageNumber: m.page });
    }
  }

  // -- Resolve pending cross-links -----------------------------------------
  // jsPDF's link() writes a clickable rectangle on a specific page.
  // We queued rectangles with named anchors during render; now we
  // know every anchor's page, so flush them.
  for (const link of pendingLinks) {
    const destPage = destinations.get(link.targetAnchor);
    if (!destPage) continue;
    doc.setPage(link.sourcePage);
    doc.link(link.x, link.y, link.w, link.h, { pageNumber: destPage });
  }

  // -- Footers (paginated now that total is known) -------------------------

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    footer(i, total);
  }

  const today = new Date().toISOString().slice(0, 10);
  const filename = `${slugify(caseData.name)} - Full Dossier - ${today}.pdf`;
  const blob = doc.output("blob");
  triggerDownload(blob, filename);

  return {
    pages: total,
    attachmentsIncluded,
    attachmentsFailed,
  };

  // ----- Inner helpers (captured state: doc, y, etc.) ---------------------

  function renderVolumeBrief(entry: VolumeBriefEntry): void {
    const { volume, summary: vsum, keyFacts: vfacts } = entry;
    const title = volume.title?.trim()
      ? `Volume ${volume.volume_number} — ${volume.title.trim()}`
      : `Volume ${volume.volume_number}`;
    h2(title);
    // Compute from actual record_dates rather than using the volume
    // row's own first_record_date / last_record_date columns, which
    // tend to reflect lifecycle timestamps (creation / last update)
    // not the content span.
    const { first, last } = volumeDateRange(records, volume.id);
    const range =
      first && last
        ? first === last
          ? formatLong(first)
          : `${formatLong(first)} to ${formatLong(last)}`
        : "Date range not available";
    text(`${range} · ${volume.record_count} records`, {
      size: 9.5,
      color: COLOR_MUTED,
    });
    if (vsum?.trim()) {
      // Scrub baked-in "at 2:00:33 PM CDT" style timestamps from
      // pre-timezone-fix cached summaries.
      text(stripProseTimestamps(vsum), { size: 11 });
    } else {
      text("No summary cached for this volume.", {
        size: 10.5,
        italic: true,
        color: COLOR_MUTED,
      });
    }
    if (vfacts.length > 0) {
      // Short bulleted list — restored from the previous prose
      // run-on. Matches the main-body Key Facts section's
      // type-then-fact rhythm without forcing a nested table.
      text("Key facts", { size: 10.5, bold: true });
      for (const f of vfacts) {
        text(`• ${KEY_FACT_TYPE_LABEL[f.type] ?? f.type}: ${f.fact}`, {
          size: 10,
          indent: 8,
        });
      }
    }
    y += 6;
  }

  // Appendix A — a bordered index table. Each row is a clickable link
  // to that record's full entry (record:N) in Appendix B.
  function renderRecordIndex(recs: CaseRecord[]): void {
    const cols = [
      { title: "#", pct: 5 },
      { title: "Date", pct: 13 },
      { title: "Type", pct: 12 },
      { title: "From", pct: 19 },
      { title: "To", pct: 19 },
      { title: "Subject", pct: 24 },
      { title: "Sig.", pct: 8 },
    ];
    const widths = cols.map((c) => (c.pct / 100) * maxW);
    const rowH = 16;
    const padX = 4;

    const drawHeaderRow = (): void => {
      doc.setFillColor(...COLOR_RECORD_HEADER);
      doc.setDrawColor(...COLOR_BORDER);
      doc.setLineWidth(0.4);
      doc.rect(MARGIN, y, maxW, rowH, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...COLOR_HEADING);
      let x = MARGIN;
      for (let i = 0; i < cols.length; i++) {
        doc.text(cols[i]!.title, x + padX, y + 11);
        x += widths[i]!;
      }
      y += rowH;
    };

    drawHeaderRow();
    for (let i = 0; i < recs.length; i++) {
      const r = recs[i]!;
      const index = i + 1;
      if (y + rowH > h - MARGIN - 24) {
        doc.addPage();
        y = MARGIN;
        drawHeaderRow();
      }
      const cells = [
        `#${index}`,
        formatShort(r.record_date),
        typeLabelFor(r),
        r.from_addr ?? "",
        r.to_addr ?? "",
        r.subject ?? "",
        significanceLabel(r.significance),
      ];
      doc.setDrawColor(...COLOR_BORDER);
      doc.setLineWidth(0.3);
      doc.rect(MARGIN, y, maxW, rowH);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(...COLOR_TEXT);
      let x = MARGIN;
      for (let c = 0; c < cells.length; c++) {
        const wcol = widths[c]!;
        const line = doc.splitTextToSize(cells[c] || "", wcol - padX * 2)[0] ??
          "";
        // First column in link colour to signal the row is clickable.
        if (c === 0) doc.setTextColor(...COLOR_LINK);
        doc.text(line, x + padX, y + 11);
        if (c === 0) doc.setTextColor(...COLOR_TEXT);
        x += wcol;
      }
      // Whole row links to the record's full entry in Appendix B.
      pendingLinks.push({
        sourcePage: currentPage(),
        x: MARGIN,
        y,
        w: maxW,
        h: rowH,
        targetAnchor: `record:${index}`,
      });
      y += rowH;
    }
    y += 10;
  }

  // Fit an image to the content width (and page height), paginating
  // when needed, then advance y. Returns false if jsPDF rejected it.
  function placeImage(
    dataUrl: string,
    naturalW: number,
    naturalH: number,
  ): boolean {
    const maxImgH = h - MARGIN * 2 - 24;
    let drawW = maxW;
    let drawH =
      naturalW > 0 && naturalH > 0
        ? naturalH * (maxW / naturalW)
        : maxW * 0.6;
    if (drawH > maxImgH) {
      const s = maxImgH / drawH;
      drawW *= s;
      drawH *= s;
    }
    // New page if the image won't fit in the remaining space.
    if (y + drawH > h - MARGIN - 24) {
      doc.addPage();
      y = MARGIN;
    }
    try {
      doc.addImage(
        dataUrl,
        undefined as unknown as string,
        MARGIN,
        y,
        drawW,
        drawH,
      );
      y += drawH + 8;
      return true;
    } catch {
      return false;
    }
  }

  // Embed every attachment of a record inline: images directly, PDF
  // pages rendered via pdfjs, audio noted (transcript is the record
  // body), everything else noted as a binary file. One fetch at a time
  // to keep memory bounded; failures are noted inline, never fatal.
  async function renderRecordAttachments(r: CaseRecord): Promise<void> {
    // Build the source list: the legacy/primary file first, then the
    // attachments table rows.
    interface Src {
      key: string;
      mime: string;
      name: string;
      hasText: boolean;
    }
    const srcs: Src[] = [];
    if (r.file_r2_key) {
      srcs.push({
        key: r.file_r2_key,
        mime: r.file_mime_type ?? "",
        name: r.file_name ?? "attachment",
        hasText: false,
      });
    } else if (r.image_r2_key) {
      srcs.push({
        key: r.image_r2_key,
        mime: "image/*",
        name: r.file_name ?? "image",
        hasText: false,
      });
    }
    for (const a of (r.attachments ?? []) as RecordAttachment[]) {
      srcs.push({
        key: a.r2_key,
        mime: a.mime_type ?? "",
        name: a.filename,
        hasText: a.has_text === 1,
      });
    }
    if (srcs.length === 0) return;

    y += 6;
    text("Attachments", { size: 10.5, bold: true, color: COLOR_ACCENT });

    for (const s of srcs) {
      const url = fileUrl(caseData.id, s.key);
      const isImage = s.mime.startsWith("image/");
      const isPdf = s.mime === "application/pdf" || /\.pdf$/i.test(s.name);
      const isAudio = s.mime.startsWith("audio/");

      if (isImage) {
        const img = await fetchImageAsDataUrl(url, authHeaders);
        if (img) {
          text(s.name, { size: 9, color: COLOR_MUTED });
          if (placeImage(img.dataUrl, img.naturalWidth, img.naturalHeight)) {
            attachmentsIncluded += 1;
          } else {
            attachmentsFailed += 1;
            text("[Attachment could not be embedded]", {
              size: 9,
              italic: true,
              color: COLOR_MUTED,
            });
          }
        } else {
          attachmentsFailed += 1;
          text(`${s.name} — [Attachment could not be loaded]`, {
            size: 9,
            italic: true,
            color: COLOR_MUTED,
          });
        }
      } else if (isPdf) {
        const bin = await fetchBinary(url, authHeaders);
        if (!bin) {
          attachmentsFailed += 1;
          text(`${s.name} — [Attachment could not be loaded]`, {
            size: 9,
            italic: true,
            color: COLOR_MUTED,
          });
          continue;
        }
        const rendered = await renderPdfPages(bin.bytes, { maxPages: 30 });
        if (!rendered || rendered.pages.length === 0) {
          attachmentsFailed += 1;
          text(`${s.name} — [PDF pages could not be rendered]`, {
            size: 9,
            italic: true,
            color: COLOR_MUTED,
          });
          continue;
        }
        const note =
          rendered.totalPages > rendered.pages.length
            ? `${s.name} (showing ${rendered.pages.length} of ${rendered.totalPages} pages)`
            : `${s.name} (${rendered.pages.length} page${rendered.pages.length === 1 ? "" : "s"})`;
        text(note, { size: 9, color: COLOR_MUTED });
        let pageOk = false;
        for (const pg of rendered.pages) {
          if (placeImage(pg.dataUrl, pg.width, pg.height)) pageOk = true;
          else
            text("[A PDF page could not be embedded — skipped]", {
              size: 9,
              italic: true,
              color: COLOR_MUTED,
            });
        }
        if (pageOk) attachmentsIncluded += 1;
        else attachmentsFailed += 1;
      } else if (isAudio) {
        text(
          `Audio: ${s.name}${s.hasText || (r.body_text ?? "").trim() ? " — transcript included above" : ""}`,
          { size: 9.5, color: COLOR_MUTED },
        );
      } else {
        text(`Attachment: ${s.name} (binary file)`, {
          size: 9.5,
          color: COLOR_MUTED,
        });
      }
    }
  }

  async function renderFullRecord(
    r: CaseRecord,
    index: number,
  ): Promise<void> {
    // Each record starts on a fresh page — anchors line up with the
    // bookmark/TOC link destinations exactly.
    if (currentPage() > 1) {
      doc.addPage();
      y = MARGIN;
    }
    const anchor = `record:${index}`;
    registerAnchor(anchor);
    recordMarks.push({
      label: recordBookmarkLabel(index, r),
      page: currentPage(),
      anchor,
    });

    // Gray header band: "Record #N" left, metadata right.
    const bandH = 18;
    doc.setFillColor(...COLOR_RECORD_HEADER);
    doc.rect(MARGIN, y, maxW, bandH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...COLOR_HEADING);
    doc.text(`Record #${index}`, MARGIN + 6, y + 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLOR_MUTED);
    doc.text(
      `${formatLong(r.record_date)} · ${typeLabelFor(r)} · ${directionLabel(r)} · ${significanceLabel(r.significance)}`,
      w - MARGIN - 6,
      y + 13,
      { align: "right" },
    );
    y += bandH + 8;

    if (r.subject) text(r.subject, { size: 13, bold: true });
    const header: string[] = [];
    if (r.from_addr) header.push(`From: ${r.from_addr}`);
    if (r.to_addr) header.push(`To: ${r.to_addr}`);
    if (r.forwarded_via) header.push(`Via: ${r.forwarded_via}`);
    if (r.tags && r.tags.trim())
      header.push(`Tags: ${r.tags.split(",").join(", ")}`);
    for (const line of header) text(line, { size: 9.5, color: COLOR_MUTED });

    y += 4;
    // Body in serif at 11pt, preserving the source line breaks.
    const body = r.body_text ?? r.ocr_text ?? "";
    if (body.trim()) {
      for (const para of body.trim().split(/\r?\n/)) {
        if (para.trim()) text(para, { size: 11, serif: true });
        else y += LINE * 0.5;
      }
    } else {
      text("(no body content)", {
        size: 10,
        italic: true,
        color: COLOR_MUTED,
      });
    }

    if (r.notes && r.notes.trim())
      text(`Notes: ${r.notes.trim()}`, { size: 10, color: COLOR_ACCENT });

    if (r.is_edited === 1) {
      text("(content edited from original)", {
        size: 9,
        italic: true,
        color: COLOR_MUTED,
      });
    }

    // Inline attachments (images / PDF pages / audio / other).
    await renderRecordAttachments(r);
  }
}
